import { HttpClient } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { ApiCallModel } from '@flocasts/experience-service-types'
import { catchError, Observable, of } from 'rxjs'
import { LOGGER_SERVICE } from '../logger/logger.config'
import { LoggerService } from '../logger/logger.interface'
import { cleanUrlFromApi } from './data.utility'

/**
 * Generic data service used with the BFF API endpoint
 */
@Injectable({
  providedIn: 'root'
})
export class DataService {
  constructor(private readonly http: HttpClient, @Inject(LOGGER_SERVICE) private readonly logger: LoggerService) {}

  /**
   * Makes a GET request with the provided endpoint.
   * @param endpoint The endpoint returned from the BFF as a link with style 'paginate'.
   * @returns An observable of the generic type.
   */
  public get<T>(endpoint: string): Observable<T | undefined> {
    return this.http.get<T>(cleanUrlFromApi(endpoint)).pipe(
      catchError(err => {
        this.logger.error(err.message, err.error)
        return of(undefined)
      })
    )
  }

  /**
   * Makes dynamic requests based on the ApiCallModel set by the BFF.
   * @param apiCall The apiCall provided by the BFF.
   * @returns The type specified by the caller.
   */
  public request<T>(apiCall: ApiCallModel): Observable<T | undefined> {
    switch (apiCall.method) {
      case 'GET':
        return this.http.get<T>(apiCall.endpoint).pipe(
          catchError(err => {
            this.logger.error(err.message, err.error)
            return of(undefined)
          })
        )
      case 'DELETE':
        return this.http.delete<T>(apiCall.endpoint).pipe(
          catchError(err => {
            this.logger.error(err.message, err.error)
            return of(undefined)
          })
        )
      case 'POST':
        return this.http.post<T>(apiCall.endpoint, JSON.parse(apiCall.payload)).pipe(
          catchError(err => {
            this.logger.error(err.message, err.error)
            return of(undefined)
          })
        )
      default:
        return of(undefined)
    }
  }
}
